import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import { useNavigate } from 'react-router-dom';

const PrayersManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchScale, setSearchScale] = useState('');

  useEffect(() => {
    if (!currentUser?.isSuperAdmin) return;
    setLoading(true);
    setError(null);
    const fetchPrayers = async () => {
      try {
        const q = query(collection(db, 'prayers'), orderBy('missionalScale', 'asc'));
        const snapshot = await getDocs(q);
        setPrayers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setError('Failed to load prayers.');
      } finally {
        setLoading(false);
      }
    };
    fetchPrayers();
  }, [currentUser]);

  if (!currentUser?.isSuperAdmin) {
    return <div className="p-8 text-center text-red-500">Access denied. Super admin only.</div>;
  }

  const filtered = prayers.filter(prayer => {
    const matchesText = search === '' || (prayer.prayerText || '').toLowerCase().includes(search.toLowerCase());
    const matchesScale = searchScale === '' || String(prayer.missionalScale) === searchScale;
    return matchesText && matchesScale;
  });

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Prayer Management</h1>
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
        <input
          type="text"
          placeholder="Search prayers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded px-3 py-2 w-full md:w-1/2"
        />
        <input
          type="number"
          placeholder="Missional Scale"
          value={searchScale}
          onChange={e => setSearchScale(e.target.value)}
          className="border rounded px-3 py-2 w-full md:w-1/4"
        />
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
          onClick={() => navigate('/add-prayer')}
        >
          + Add Prayer
        </button>
        <button
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-500"
          onClick={() => navigate('/bulk-prayer-upload')}
        >
          Bulk Upload Prayers
        </button>
      </div>
      {loading ? (
        <div className="text-center py-8">Loading prayers...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded shadow">
            <thead>
              <tr className="bg-gray-100 text-gray-700 text-left">
                <th className="py-2 px-3">Prayer</th>
                <th className="py-2 px-3">Missional Scale</th>
                <th className="py-2 px-3">Any Level</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(prayer => (
                <tr key={prayer.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 max-w-xs truncate" title={prayer.prayerText}>
                    <div className="font-semibold text-gray-800">
                      {prayer.prayerDescriptor || '(No Descriptor)'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {prayer.prayerText}
                    </div>
                  </td>
                  <td className="py-2 px-3">{prayer.missionalScale}</td>
                  <td className="py-2 px-3">{prayer.anyLevel ? 'Yes' : 'No'}</td>
                  <td className="py-2 px-3">
                    <button
                      className="text-blue-600 hover:underline mr-2"
                      onClick={() => navigate(`/edit-prayer/${prayer.id}`)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="text-center py-4 text-gray-400">No prayers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PrayersManagement; 